/**
 * Plain-text rendering of a CRM property value for the timeline's
 * non-visual surfaces: sub-item accessible names (`aria-label`) and the link
 * text of URL sub-items.
 *
 * The visible sub-item text is rendered by `PropertyValueLabel` /
 * `PropertyDiffValue`, which know every field type. This helper is the
 * string counterpart for the same value shapes, so a screen reader hears
 * "Phone Number: +44 121 234 0037" rather than "Phone Number: [object
 * Object]". Shared by the created/updated item and event renderers — keep
 * it the single owner of "what does this value sound like".
 *
 * Handled shapes, first match wins:
 * - `null` / `undefined` → `options.empty`
 * - `{ value }` PropertyValue wrapper → unwrap and recurse
 * - arrays (multi-value properties) → each element, comma-joined
 * - phone `{ country_code, national_number }` → international format
 * - quantity `{ amount, unit }` → "amount unit"
 * - `{ stage_name }` → the stage name
 * - any other object → JSON, never `String(value)` (which yields
 *   "[object Object]")
 * - primitives → `String(value)`
 */

import { formatPhoneNumber, isPhoneNumberValue } from '@/lib/crm/utils/phone';
import { type QuantityValue, unwrapPropertyValue } from '@/types/crm';

export interface TimelineValueTextOptions {
  /** Text used for `null`/`undefined` (and arrays with no non-empty entries). Defaults to ''. */
  empty?: string;
}

function isQuantityValue(value: object): value is QuantityValue {
  return 'amount' in value && 'unit' in value;
}

export function getTimelineValueText(
  value: unknown,
  options: TimelineValueTextOptions = {}
): string {
  const empty = options.empty ?? '';

  if (value === null || value === undefined) {
    return empty;
  }

  if (Array.isArray(value)) {
    const parts = value
      .map(entry => getTimelineValueText(entry))
      .filter(part => part !== '');
    return parts.length > 0 ? parts.join(', ') : empty;
  }

  if (typeof value === 'object') {
    // timeline event payload: single-level {value} wrapper — unwrap once via unwrapPropertyValue, then recurse for formatting
    const unwrapped = unwrapPropertyValue(value);
    if (unwrapped !== value) {
      return getTimelineValueText(unwrapped, options);
    }
    if (isPhoneNumberValue(value)) {
      return formatPhoneNumber(value);
    }
    if (isQuantityValue(value)) {
      return `${value.amount} ${value.unit}`;
    }
    if ('stage_name' in value) {
      return String((value as { stage_name: unknown }).stage_name);
    }
    return JSON.stringify(value);
  }

  return String(value);
}
